import { DestroyRef, Directive, inject, input, OnInit } from '@angular/core';
import { ExtractedValue } from '../models/extracted-values';
import { FieldFormControl } from '../models/extraction-form-models';
import { ExtractionField } from '../pages/extraction-page/extraction-page.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CtDocument, ClaimsToastService } from '@ps-gui/claims-terminal-shared';
import { OcrSelectionService } from '../service/ocr/ocr-selection.service';
import { ExtractedLocationStoreService } from '../service/overlay/extracted-location-store.service';
import { OcrHighlightService } from '../service/overlay/ocr-highlight.service';
import { parseValue } from '../utils/type-parsers';

@Directive({
  selector: '[ctExtractionOcrFormInput]',
})
export class OcrFormInputDirective implements OnInit {
  private readonly ocrSelectionService = inject(OcrSelectionService);
  private readonly toastService = inject(ClaimsToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly extractedLocationStoreService = inject(ExtractedLocationStoreService);
  private readonly highlightService = inject(OcrHighlightService);

  readonly activeField = input<ExtractionField>();
  readonly currentDocument = input<CtDocument>();

  ngOnInit(): void {
    this.ocrSelectionService.selections
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selection) => {
        const currentField = this.activeField();
        if (!currentField) return;

        const control = currentField.control;
        if (!control) return;

        const fieldModel = currentField.model;

        const joinedString = selection.words.join(' ');
        const parseResult = parseValue(joinedString, fieldModel);

        if (parseResult.error) {
          this.toastService.error(parseResult.error);
          return;
        }

        const document = this.currentDocument();

        if (selection.mode === 'append') {
          this.augmentField(control, parseResult.value);

          if (document) {
            this.extractedLocationStoreService.appendExtractedLocation(
              document.id,
              selection.pageId,
              currentField.idOverride ?? fieldModel.key,
              selection.wordRects,
            );
          }
        } else {
          this.patchField(control, parseResult.value);

          if (document) {
            this.extractedLocationStoreService.replaceExtractedLocation(
              document.id,
              selection.pageId,
              currentField.idOverride ?? fieldModel.key,
              selection.wordRects,
            );
          }
        }

        if (document) {
          this.highlightService.setFieldHighlight(
            document.id,
            currentField.idOverride ?? fieldModel.key,
          );
        }
      });
  }

  patchField(control: FieldFormControl, processedValue: ExtractedValue) {
    control.patchValue(processedValue);
  }

  augmentField(control: FieldFormControl, processedValue: ExtractedValue) {
    const currentValue: ExtractedValue = control.value;
    control.patchValue(
      currentValue ? String(currentValue) + ' ' + String(processedValue) : processedValue,
    );
  }
}
